














































































































































import Vue from 'vue';
import SideTabs from '@/parts/mypage/SideTabs.vue';
import PageTabs from '@/parts/mypage/Tabs.vue';
import PrevNext from '@/parts/mypage/PrevNext.vue';
import { getColorClass } from '@/utils/styleUtils';

import { GetUserReviewsResponse, TenderOrder, UserReview } from '@/api/dto/UserReview';
import { getUserDeals } from '@/api/UserReview';
import { checkReviewTimeLimit, checkTdsNotExecuted } from "@/api/Review";
import moment from "moment"
import { compareByDate, getDaysCountToEndDate, Ordering } from '@/utils/momentUtils'
import { Completed, Confirmation, Draft, InReview, Pending } from '@/api/dto/DealStatus';
import { PurchaseType } from '@/api/dto/PurchaseType';

interface Data {
  tabs: { mode: string, text: string }[];
  dealsData: GetUserReviewsResponse;
  perPage: number;
  /** 入札した依頼 - ページ位置 */
  pageAcc: number;
  /** 受注した依頼 - レビュー中 */
  filter1: boolean;
  /** 受注した依頼 - 確認・質問 */
  filter2: boolean;
  /** 受注した依頼 - 完了 */
  filter3: boolean;
  /** 入札した依頼 - ページ位置 */ 
  pageTendered: number;
  /** 作成した依頼 - ページ位置 */ 
  pageMyDeal: number;
  /** 作成した依頼 - 下書き・期限切れ */ 
  filter4: boolean;
  /** 作成した依頼 - 受注待ち */
  filter5: boolean;
  /** 作成した依頼 - レビュー中 */
  filter6: boolean;
  /** 作成した依頼 - 確認・評価 */
  filter7: boolean;
  /** 作成した依頼 - 完了 */
  filter8: boolean;
  today: moment.Moment;
}

/**
 * dealStatus が Completed の時は後ろにする
 */
const ordStatus = (a: UserReview, b: UserReview): Ordering => {
  if (b.dealStatus === Completed) {
    return -1;
  } else if (a.dealStatus === Completed) {
    return 1;
  } else {
    return 0;
  }
}

/**
 * ステータスでソートした後に取引開始時刻降順でソートする
 */
const compareDealStatusThenDealStartTime = (
  a: UserReview,
  b: UserReview
): Ordering => {
  const status = ordStatus(a, b);
  return status !== 0
    ? status
    : (-compareByDate(a.dealStartTime, b.dealStartTime) as Ordering);
};

/**
 * ステータスでソートした後に作成時刻降順でソートする
 */
const compareDealStatusThenCreateDate = (
  a: UserReview,
  b: UserReview
): Ordering => {
  const status = ordStatus(a, b);
  return status !== 0
    ? status
    : (-compareByDate(a.createDate, b.createDate) as Ordering);
};

/**
 * ページ部分を切り出す
 * @param arr: 配列
 * @param pageIndex: ページ番号(1-origin)
 * @param perPage: 1ページあたりの要素数
 */
function pageView<T>(arr: T[], pageIndex: number, perPage: number): T[] {
  const start = Math.min((pageIndex - 1) * perPage, arr.length);
  const end = Math.min(start + perPage, arr.length);
  return arr.slice(start, end);
}

export default Vue.extend({
  name: 'DealsManagement',
  components: {
    SideTabs,
    PageTabs,
    PrevNext,
  },
  props: {
    modeName: String,
  },
  data(): Data {
    this.$store.commit("setMode", {
      mode: ["mydeals", "tendered", "accepted"].includes(this.modeName)
        ? this.modeName
        : "accepted",
    });
    return {
      tabs: [
        { mode: 'accepted', text: '受注した依頼' },
        { mode: 'tendered', text: '入札した依頼' },
        { mode: 'mydeals', text: '作成した依頼' },
      ],
      dealsData: {
        receiveOrder: [],
        purchaseOrder: [],
        tenderOrder: []
      },
      perPage: 10, // 1ページ毎の表示件数、共通
      // 受注した依頼
      pageAcc: 1,
      filter1: true,
      filter2: true,
      filter3: true,
      // 入札した依頼
      pageTendered: 1,
      // 作成した依頼
      pageMyDeal: 1,
      filter4: true,
      filter5: true,
      filter6: true,
      filter7: true,
      filter8: true,
      today: moment(),
    }
  },
  computed: {
    mode() {
      return this.$store.state.mode;
    },
    /**
     * ソートされた受注した依頼
     */
    orderedReviews(): UserReview[] {
      return this.dealsData.receiveOrder
        .sort(compareDealStatusThenDealStartTime);
    },
    /**
     * フィルタリングされてソートされた作成した依頼
     */
    filteredOrderedReviews(): UserReview[] {
      this.resetPage('accepted');
      const filters: Record<string, boolean> = {
        [InReview]: this.filter1,
        [Confirmation]: this.filter2,
        [Completed]: this.filter3,
      }
      const statuses = Object.keys(filters).filter(key => filters[key]);
      return this.orderedReviews
        .filter(v => statuses.includes(v.dealStatus));
    },
    // タブ1のページネーション
    pagedFilteredOrderedReviews(): UserReview[] {
      return pageView(this.filteredOrderedReviews, this.pageAcc, this.perPage);
    },
    reviewingCount(): number {
      return this.orderedReviews
        .filter((v: UserReview) => (v.dealStatus === InReview)).length;
    },
    contactCount(): number {
      return this.orderedReviews
        .filter((v: UserReview) => (v.dealStatus === Confirmation)).length;
    },
    doneCount(): number {
      return this.orderedReviews
        .filter((v: UserReview) => (v.dealStatus === Completed)).length;
    },
    totalPageAcc(): number {
      return Math.ceil(this.filteredOrderedReviews.length / this.perPage);
    },
    // タブ2
    /** 「入札した依頼」タブに表示する依頼 */
    tenderedReviews(): TenderOrder[] {
      this.resetPage("tendered");

      // 掲載期限降順でソート
      const tenderedReviews = this.dealsData.tenderOrder.sort(
        (a, b) =>
          -compareByDate(a.publishedPeriod, b.publishedPeriod) as Ordering
      );
      return pageView(tenderedReviews, this.pageTendered, this.perPage);
    },
    /** 入札した依頼のページ数 */
    totalPageTendered(): number {
      return Math.ceil(this.tenderedReviews.length / this.perPage);
    },
    // タブ3
    purchasedReviews(): UserReview[] {
      return this.dealsData.purchaseOrder
        .sort(compareDealStatusThenCreateDate);
    },
    filteredPurchasedReviews(): UserReview[] {
      this.resetPage('mydeals');
      const filters: Record<string, boolean> = {
        [Draft]: this.filter4,
        [Pending]: this.filter5,
        [InReview]: this.filter6,
        [Confirmation]: this.filter7,
        [Completed]: this.filter8,
      }
      const statuses = Object.keys(filters).filter((key) => filters[key]);
      return this.purchasedReviews.filter((v) =>
        statuses.includes(v.dealStatus)
      );
    },
    // タブ3のページネーション
    pagedFilteredPurchasedReviews(): UserReview[] {
      return pageView(this.filteredPurchasedReviews, this.pageMyDeal, this.perPage);
    },
    expiredCount(): number {
      return this.purchasedReviews
        .filter((v: UserReview) => (v.dealStatus === Draft)).length;
    },
    waitingCount(): number {
      return this.purchasedReviews
        .filter((v: UserReview) => (v.dealStatus === Pending)).length;
    },
    /**
     * 入札ありの件数
     * NOTE: purchaseType=1 && 入札依頼件数が1件以上の依頼
     */
    tenderedCount(): number {
      return this.purchasedReviews.filter(
        ({ purchaseType, tenderCount, dealStatus }) =>
          purchaseType === PurchaseType.Tendered && tenderCount > 0 && dealStatus === "2"
      ).length;
    },
    inReviewCount(): number {
      return this.purchasedReviews
        .filter((v: UserReview) => (v.dealStatus === InReview)).length;
    },
    confirmCount(): number {
      return this.purchasedReviews
        .filter((v: UserReview) => (v.dealStatus === Confirmation)).length;
    },
    closedCount(): number {
      return this.purchasedReviews
        .filter((v: UserReview) => (v.dealStatus === Completed)).length;
    },
    totalPageMyDeal(): number {
      return Math.ceil(this.filteredPurchasedReviews.length / this.perPage);
    },
  },
  filters: {
    moment: function(date: Date) {
      const remainingDaysCount = getDaysCountToEndDate(date);
      if(remainingDaysCount < 0) {
        return `期限切れ（${moment(date).format('M月D日まで')}）`;
      } else {
        return `残り${remainingDaysCount}日`;
      }
    },
  },
  async beforeRouteEnter(to, from, next) {  
    try {
      // レビュー制限時間到達チェック
      // ※NavigationBarのRouteでも呼んでいるが、取引情報一覧取得の方が先に呼ばれてしまうため、ここで先に呼ぶ
      await checkReviewTimeLimit();
      // 3Dセキュア認証不正結果チェック
      await checkTdsNotExecuted();
      // 取引情報一覧取得
      const res = await getUserDeals();
      next(vm => (vm.$data as Data).dealsData = res);
    } catch (e) {
      next(false);
    }
  },
  methods: {
    changeMode(key: string) {
      this.$store.commit('setMode', { mode: key });
    },
    limitTime(date: Date) {
      var limitTime = moment(date).add(48, 'hours');
      var diff = moment(limitTime).diff(this.today) + 1000; // 差をミリ秒で取得
      var duration = moment.duration(diff); // ミリ秒からdurationオブジェクトを生成
      var days = Math.floor(duration.asDays());
      var hours   = duration.hours();
      var minutes = duration.minutes();
      if (days > 0) {
        return days + "日以上";
      } else if (hours > 0) {
        return hours + '時間';
      } else if(minutes > 0){
        return minutes + '分';
      } else {
        return '1分未満';
      }
    },
    accStatus(dealStatus: string, dealStartTime: string) {
      switch(dealStatus) {
        case InReview:
          if (dealStartTime == '') {
            return "取引開始待ち"
          } else {
            return "レビュー中"
          }
        case Confirmation:
          return "確認・質問"
      }
    },
    accNote(dealStatus: string, dealStartTime: string) {
      switch(dealStatus) {
        case InReview:
          if (dealStartTime == '') {
            return ""
          } else {
            return "残り" + this.limitTime(new Date(dealStartTime))
          }
        case Confirmation:
          return ""
      }
    },
    diffDateDisp(expire_date: Date) {
      if ( moment(expire_date).diff(this.today,'days') < 0 ) {
        return "期限切れ";
      }
      return "下書き";
    },
    diffDateColor(expire_date: Date) {
      if ( moment(expire_date).diff(this.today,'days') < 0 ) {
        return "expired";
      }
      return "";
    },
    /** 期限切れかどうか */
    expired(date: Date) {
      return moment(date).diff(this.today,'days') < 0;
    },
    /** レビューカードの遷移先 URL */
    mydealLink({
      dealStatus, 
      dealId,
      purchaseType,
      tenderCount,
    }: UserReview) {
      switch (dealStatus) {
        case Draft:
          return { name: "ReviewEdit", params: { dealId: dealId } };
        case Pending:
          if (purchaseType === PurchaseType.Tendered && tenderCount > 0) {
            // NOTE: 依頼種別が「入札」&& 入札件数が1件以上ある場合、「入札状況一覧画面」へ遷移
            return { name: "Tender", params: { dealId: dealId } };
          } else {
            return { name: "Review", params: { dealId: dealId } };
          }
        default:
          return { name: "Deal", params: { dealId: dealId } };
      }
    },
    mydealStatus(dealStatus: string, dealStartTime: string) {
      switch(dealStatus) {
        case InReview:
          if (dealStartTime == null || dealStartTime == '') {
            return "取引開始待ち"
          } else {
            return "レビュー中"
          }
        case Confirmation:
          return "確認・評価"
      }
    },
    /** 作成した依頼：レビューカード上に入札件数ラベルを表示するかどうか */
    visibleTenderIcon(deal: UserReview) {
      return (
        deal.purchaseType === PurchaseType.Tendered &&
        deal.tenderCount > 0 &&
        deal.dealStatus === Pending
      );
    }, 
    setColorClass(lang: string) {
      return getColorClass(lang);
    },
    onPageChange(currentPage: number) {
      if (this.mode === 'accepted') {
        this.pageAcc = currentPage;
      } else if (this.mode === 'tendered') {
        this.pageTendered = currentPage;
      } else if (this.mode === 'mydeals') {
        this.pageMyDeal = currentPage;
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    resetPage(mode: string) {
      if (mode === 'accepted') {
        this.pageAcc = 1;
      } else if(mode === 'tendered') {
        this.pageTendered = 1;
      } else if (mode === 'mydeals') {
        this.pageMyDeal = 1;
      }
    },
  }
})
